import { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { common, financedEmissions } from "../../../i18n";
import {
  createBorrower,
  createLoan,
  createLoanSnapshot,
  getBorrowerDetail,
  getBorrowers,
  setFinancialData,
  updateBorrower,
  updateFinancialData,
  updateLoanSnapshot,
} from "../../../service/cat15.service";
import {
  BorrowerDetail,
  BorrowerListItem,
  BorrowerLoan,
  FinancialYear,
  LoanSnapshot,
} from "../../../models/dto/cat15/cat15.types";
import { UpdateBorrowerBody } from "../../../models/dto/cat15/cat15.api";
import alertService from "../../../components/alert/alertService";
import { TabMenu, TabMenuData } from "../../../components/tabMenu/TabMenu";
import ListingStatusChip from "../../../components/cat15/ListingStatusChip";
import { sectionLabel } from "../../../components/cat15/labels";
import BorrowerSectorTable, { isLegalEntity } from "./BorrowerSectorTable";
import PortfolioOverview from "./PortfolioOverview";
import OverviewSelect, { ALL } from "./OverviewSelect";
import {
  FeAddIcon,
  FeSearchIcon,
  FeStatusDotIcon,
  FeStylusIcon,
} from "../../../components/cat15/FeIcons";
import { LayoutContext } from "../../../contexts/LayoutContext";
import DetailSummary from "./DetailSummary";
import DetailLoans from "./DetailLoans";
import DetailFinancial from "./DetailFinancial";
import DetailEmissions from "./DetailEmissions";
import AddBorrowerModal, { AddBorrowerForm } from "./AddBorrowerModal";
import EditBorrowerModal, { EditBorrowerForm } from "./EditBorrowerModal";
import AddLoanModal, { AddLoanForm } from "./AddLoanModal";
import AddSnapshotModal, { AddSnapshotForm } from "./AddSnapshotModal";
import EditSnapshotModal, { EditSnapshotForm } from "./EditSnapshotModal";
import FinancialDataModal, { FinancialDataForm } from "./FinancialDataModal";

type SubTab = "summary" | "loans" | "financial" | "emissions";

const ProvidedByBorrowerTag = () => {
  const { t } = useTranslation([financedEmissions, common], { nsMode: "fallback" });
  return (
    <Chip
      size="small"
      variant="outlined"
      label={t("Provided-By-Borrower")}
      sx={{ borderRadius: "16px", color: "var(--gray9)", borderColor: "var(--gray4)" }}
    />
  );
};

/** Data Status pill — Figma 7458:14003: 24px tall, 4/12 padding, 20px radius, 12px dot in a 24px box. */
const BorrowerStatusPill = ({ active }: { active: boolean }) => {
  const { t } = useTranslation([financedEmissions, common], { nsMode: "fallback" });
  const color = active ? "var(--green)" : "var(--status-draft-inactive-text)";
  return (
    <Box
      display="flex"
      alignItems="center"
      height="24px"
      pl="4px"
      pr="12px"
      borderRadius="20px"
      bgcolor={active ? "var(--status-bg)" : "var(--status-draft-inactive-bg)"}
      flexShrink={0}
    >
      <Box width="24px" height="24px" display="flex" alignItems="center" justifyContent="center">
        <FeStatusDotIcon sx={{ fontSize: "12px", color }} />
      </Box>
      <Typography fontSize="16px" fontWeight={400} lineHeight="24px" color={color}>
        {active ? t("Status-Active") : t("Status-Inactive")}
      </Typography>
    </Box>
  );
};

/** 1px var(--gray2) rule between the header info segments (Figma 7457:13833). */
const InfoDivider = () => (
  <Box width="1px" alignSelf="stretch" bgcolor="var(--gray2)" flexShrink={0} />
);

/** Body/Text1 — Regular 14/24 dark ink (Figma 7457:13784). */
const infoText = {
  fontSize: "14px",
  fontWeight: 400,
  lineHeight: "24px",
  color: "var(--dark)",
  whiteSpace: "nowrap",
} as const;

const MasterDetail = () => {
  const { t, i18n } = useTranslation([financedEmissions, common], {
    nsMode: "fallback",
  });

  const [list, setList] = useState<BorrowerListItem[]>([]);
  // The selected borrower lives in the URL, so the eye icon opens a real page:
  // browser back works, and the detail is linkable. No borrowerId = the list.
  const { borrowerId } = useParams<{ borrowerId: string }>();
  // The id goes into API paths, so anything but digits (e.g. a crafted ../) is treated as no borrower.
  const selectedId = borrowerId && /^\d+$/.test(borrowerId) ? borrowerId : null;
  const navigate = useNavigate();
  const openBorrower = (id: string) =>
    navigate(`/financed-emissions/borrowers/${id}`);
  const backToList = () => navigate("/financed-emissions/borrowers");
  const [detail, setDetail] = useState<BorrowerDetail | undefined>();
  const [search, setSearch] = useState("");
  const [sector, setSector] = useState<string>(ALL);
  const [borrowerType, setBorrowerType] = useState<string>(ALL);
  const [status, setStatus] = useState<string>(ALL);
  const [subTab, setSubTab] = useState<SubTab>("summary");
  const [openAddBorrower, setOpenAddBorrower] = useState(false);
  const [openEditBorrower, setOpenEditBorrower] = useState(false);
  const [openAddLoan, setOpenAddLoan] = useState(false);
  // Financial-data modal: open flag + the row being edited (null = add mode).
  const [openFinancial, setOpenFinancial] = useState(false);
  const [editingFinancial, setEditingFinancial] = useState<FinancialYear | null>(null);
  const [addingSnapshotLoan, setAddingSnapshotLoan] =
    useState<BorrowerLoan | null>(null);
  const [editingSnapshot, setEditingSnapshot] = useState<{
    loan: BorrowerLoan;
    snapshot: LoanSnapshot;
  } | null>(null);
  // After a snapshot edit we signal DetailLoans (loan id + a bumped seq) to
  // re-fetch just that loan's rows in place — no remount, so the row stays open.
  const [snapEditSeq, setSnapEditSeq] = useState(0);
  const [editedLoanId, setEditedLoanId] = useState<string | null>(null);
  const editedLoan = useMemo(
    () => (editedLoanId ? { id: editedLoanId, seq: snapEditSeq } : undefined),
    [editedLoanId, snapEditSeq],
  );

  useEffect(() => {
    getBorrowers().then(setList);
  }, []);

  // Borrower name in the page language — the header title and the last breadcrumb.
  const detailName = detail
    ? i18n.language.includes("th")
      ? detail.nameTh || detail.name
      : detail.name
    : undefined;
  const listItem = list.find((b) => b.id === selectedId);
  const { setBreadcrumbTail } = useContext(LayoutContext);
  useEffect(() => {
    setBreadcrumbTail(selectedId ? detailName : undefined);
  }, [selectedId, detailName, setBreadcrumbTail]);
  useEffect(() => () => setBreadcrumbTail(undefined), [setBreadcrumbTail]);

  useEffect(() => {
    // Clear the previous borrower so their figures never show under the next one.
    setDetail(undefined);
    if (!selectedId) return;
    setSubTab("summary");
    // Drop a late reply for a borrower the user has already left.
    let active = true;
    getBorrowerDetail(selectedId).then((d) => active && setDetail(d));
    return () => {
      active = false;
    };
  }, [selectedId]);

  // One option per section code, so a language switch relabels the options without losing the chosen one.
  const sectionOptions = useMemo(
    () =>
      Array.from(
        new Map(
          list.map((b) => [b.sectionCode, { sectionCode: b.sectionCode, sector: b.sector, sectorTh: b.sectorTh }]),
        ).values(),
      ),
    [list],
  );

  // Tax IDs are stored both bare (seeded) and dashed (typed in Add Borrower), so match on digits only.
  const searchDigits = search.replace(/\D/g, "");
  const filtered = list.filter((b) => {
    const q = search.toLowerCase();
    const matchSearch =
      !search ||
      b.name.toLowerCase().includes(q) ||
      !!b.nameTh?.toLowerCase().includes(q) ||
      !!b.nameEn?.toLowerCase().includes(q) ||
      (!!searchDigits && b.taxId.replace(/\D/g, "").includes(searchDigits));
    const matchSector = sector === ALL || b.sectionCode === sector;
    const matchType =
      borrowerType === ALL ||
      (borrowerType === "legal") === isLegalEntity(b.taxId);
    const matchStatus =
      status === ALL || (status === "active") === b.activated;
    return matchSearch && matchSector && matchType && matchStatus;
  });

  const resetFilters = () => {
    setSearch("");
    setSector(ALL);
    setBorrowerType(ALL);
    setStatus(ALL);
  };

  // The service already alerted any refusal; the second then() argument absorbs only that rejection, so it never surfaces as uncaught.
  const handleAddBorrower = (values: AddBorrowerForm) => {
    createBorrower({
      taxId: values.taxId,
      nameEn: values.nameEN,
      nameTh: values.nameTH || undefined,
      sectorIsic: values.sectorIsic,
      sectorTsic: values.sectorTsic || undefined,
      // Left out when not provided, so the borrower is asked for it.
      ...(values.listingStatus ? { listingStatus: values.listingStatus } : {}),
    }).then((created) => {
      setDetail(created);
      getBorrowers().then(setList);
      openBorrower(created.id);
      alertService.success(t("Borrower-Added", { name: created.name }));
    }, () => undefined);
  };

  const handleEditBorrower = (changed: Partial<EditBorrowerForm>) => {
    if (!selectedId) return;
    const body: UpdateBorrowerBody = {};
    if (changed.nameEN !== undefined) body.nameEn = changed.nameEN;
    // nameTh may be "" so the Thai name can be cleared.
    if (changed.nameTH !== undefined) body.nameTh = changed.nameTH;
    if (changed.sectorIsic) body.sectorIsic = changed.sectorIsic;
    if (changed.sectorTsic) body.sectorTsic = changed.sectorTsic;
    if (changed.listingStatus) body.listingStatus = changed.listingStatus;
    updateBorrower(selectedId, body).then((updated) => {
      setDetail(updated);
      getBorrowers().then(setList);
      alertService.success(t("Borrower-Updated", { name: updated.name }));
    }, () => undefined);
  };

  const parseAmount = (v: string) => Number(v.replace(/,/g, "")) || 0;

  // Re-pull the selected borrower's detail after a write and sync the list's
  // loan count for that row.
  const refreshDetail = (id: string) =>
    getBorrowerDetail(id).then((updated) => {
      setDetail(updated);
      setList((prev) =>
        prev.map((b) =>
          b.id === updated.id ? { ...b, loanCount: updated.loanCount } : b,
        ),
      );
    });

  const handleAddLoan = (values: AddLoanForm) => {
    if (!selectedId) return;
    createLoan({
      borrowerId: Number(values.borrowerId || selectedId),
      assetClass: values.assetClass,
      loanRef: values.loanRef || undefined,
      principal: parseAmount(values.principal),
      // BE requires an origination date; default to today when the (optional)
      // field is left blank.
      originationDate: values.origination || new Date().toISOString().slice(0, 10),
      maturityDate: values.maturity || undefined,
      outstanding: values.outstanding
        ? parseAmount(values.outstanding)
        : undefined,
      // Year picker seeds the initial snapshot at that year-end.
      asOfDate: values.year ? `${values.year}-12-31` : undefined,
    }).then(() => {
      refreshDetail(selectedId);
      setSubTab("loans");
      alertService.success(t("Loan-Added", { loanRef: values.loanRef }));
    });
  };

  const handleAddSnapshot = (values: AddSnapshotForm) => {
    if (!selectedId || !addingSnapshotLoan) return;
    const loan = addingSnapshotLoan;
    createLoanSnapshot(loan.id, {
      year: Number(values.year),
      outstandingAmount: parseAmount(values.outstanding),
      asOfDate: values.asOfDate || undefined,
    }).then(() => {
      refreshDetail(selectedId);
      // Re-fetch the loan's snapshot rows in place so the new year appears.
      setEditedLoanId(loan.id);
      setSnapEditSeq((n) => n + 1);
      alertService.success(
        t("Snapshot-Added", { year: values.year, loanRef: loan.loanRef }),
      );
    });
  };

  // Add/edit a borrower's bank-entered financials. The modal collects raw baht
  // (the table shows/enters THB directly — no M-THB scaling).
  const handleFinancialSubmit = (values: FinancialDataForm, editYear: number | null) => {
    if (!selectedId) return;
    const toBaht = (s: string): number | undefined => {
      const n = parseAmount(s);
      return s.trim() !== "" && n > 0 ? n : undefined;
    };
    const figures = {
      evic: toBaht(values.evic),
      totalEquity: toBaht(values.equity),
      totalDebt: toBaht(values.debt),
      revenue: toBaht(values.revenue),
      asOfDate: values.asOfDate || undefined,
    };
    const request =
      editYear != null
        ? updateFinancialData(selectedId, editYear, figures)
        : setFinancialData(selectedId, { year: Number(values.year), ...figures });
    request.then(() => {
      refreshDetail(selectedId);
      alertService.success(t("Financial-Saved", { year: values.year }));
    });
  };

  const handleEditSnapshot = (values: EditSnapshotForm) => {
    if (!selectedId || !editingSnapshot) return;
    const { loan, snapshot } = editingSnapshot;
    // Only `outstandingAmount` is editable — per-year tco2e/dqs are computed by
    // the calculation engine and ignored by the FE.
    updateLoanSnapshot(loan.id, snapshot.year, {
      outstandingAmount: parseAmount(values.outstanding),
    }).then(() => {
      refreshDetail(selectedId);
      setEditedLoanId(loan.id);
      setSnapEditSeq((n) => n + 1);
      alertService.success(
        t("Snapshot-Updated", { year: snapshot.year, loanRef: loan.loanRef }),
      );
    });
  };

  const subTabs: TabMenuData[] = [
    { pathname: "summary", name: "Detail-Summary", label: t("Detail-Summary") },
    {
      pathname: "loans",
      name: "Detail-Linked-Loans",
      label: `${t("Detail-Linked-Loans")} (${detail?.loans.length ?? 0})`,
    },
    {
      pathname: "financial",
      name: "Detail-Financial",
      label: t("Detail-Financial"),
    },
    {
      pathname: "emissions",
      name: "Detail-Emissions",
      label: t("Detail-Emissions"),
    },
  ];

  // The ISIC-4 name follows the page language, falling back to the other language's name.
  const ownSectorLabel = (
    b: Pick<BorrowerListItem, "sectorIsicCode" | "sectorIsicNameEn" | "sectorIsicNameTh">,
  ) => {
    if (!b.sectorIsicCode) return "—";
    const name = i18n.language.includes("th")
      ? b.sectorIsicNameTh || b.sectorIsicNameEn
      : b.sectorIsicNameEn || b.sectorIsicNameTh;
    return name ? `${b.sectorIsicCode} · ${name}` : b.sectorIsicCode;
  };

  return (
    <Box display="flex" flexDirection="column" gap="20px">
      {!selectedId && (
        <>
          <PortfolioOverview />

          {/* Borrower list section (Figma 7244:21471): white, radius 8, p 20, gap 16 */}
          <Box
            display="flex"
            flexDirection="column"
            gap="16px"
            p="20px"
            borderRadius="8px"
            bgcolor="var(--white)"
          >
            {/* Filter bar (Figma 7244:21472) */}
            <Box display="flex" alignItems="center" gap="12px">
              <TextField
                size="small"
                placeholder={t("Search-Borrower")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{
                  width: "280px",
                  flexShrink: 0,
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "var(--white)",
                    borderRadius: "8px",
                    px: "12px",
                    gap: "8px",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "var(--gray2)",
                  },
                  "& .MuiOutlinedInput-input": {
                    p: "8px 0",
                    fontSize: "16px",
                    fontWeight: 400,
                    lineHeight: "24px",
                    height: "24px",
                    color: "var(--dark)",
                    "&::placeholder": { color: "var(--gray1)", opacity: 1 },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <FeSearchIcon
                      sx={{ fontSize: 24, color: "var(--gray1)" }}
                    />
                  ),
                }}
              />
              <Box display="flex" alignItems="center" gap="12px" flex={1} minWidth={0}>
                <OverviewSelect
                  value={sector}
                  onChange={setSector}
                  placeholder={t("Sector")}
                  textPr={44}
                  options={[
                    { value: ALL, label: t("Filter-All") },
                    ...sectionOptions.map((s) => ({
                      value: s.sectionCode,
                      label: sectionLabel(s, i18n.language, t("Sector-Unclassified")),
                    })),
                  ]}
                />
                <OverviewSelect
                  value={borrowerType}
                  onChange={setBorrowerType}
                  placeholder={t("Col-Borrower-Type")}
                  textPr={12}
                  options={[
                    { value: ALL, label: t("Filter-All") },
                    { value: "legal", label: t("Borrower-Type-Legal") },
                    { value: "individual", label: t("Borrower-Type-Individual") },
                  ]}
                />
                <OverviewSelect
                  value={status}
                  onChange={setStatus}
                  placeholder={t("Col-Borrower-Status")}
                  options={[
                    { value: ALL, label: t("Filter-All") },
                    { value: "active", label: t("Status-Active") },
                    { value: "inactive", label: t("Status-Inactive") },
                  ]}
                />
              </Box>
              <Box display="flex" alignItems="center" gap="12px" flexShrink={0}>
                <Button
                  variant="outlined"
                  onClick={resetFilters}
                  sx={{
                    minWidth: 0,
                    // Figma strokes the 1px border inside a 96x40 box, so the
                    // padding gives up that pixel: 1 + 11 + 72 + 11 + 1 = 96.
                    px: "11px",
                    py: "7px",
                    borderRadius: "4px",
                    border: "1px solid var(--dark)",
                    color: "var(--dark)",
                    bgcolor: "var(--white)",
                    textTransform: "none",
                    "&:hover": {
                      border: "1px solid var(--dark)",
                      bgcolor: "var(--white)",
                    },
                  }}
                >
                  <Typography
                    width="72px"
                    textAlign="center"
                    fontSize="16px"
                    fontWeight={500}
                    lineHeight="24px"
                  >
                    {t("Reset")}
                  </Typography>
                </Button>
                <Button
                  variant="contained"
                  color="green"
                  onClick={() => setOpenAddBorrower(true)}
                  startIcon={<FeAddIcon sx={{ fontSize: "24px !important" }} />}
                  sx={{
                    px: "12px",
                    py: "8px",
                    borderRadius: "4px",
                    // Figma's Add Borrower has no stroke; the theme's green
                    // variant adds one, which made it 42px tall instead of 40.
                    border: "none",
                    boxShadow: "none",
                    color: "var(--white)",
                    textTransform: "none",
                    fontSize: "16px",
                    fontWeight: 500,
                    lineHeight: "24px",
                    "& .MuiButton-startIcon": { m: 0, mr: "8px" },
                  }}
                >
                  {t("Add-Borrower")}
                </Button>
              </Box>
            </Box>

            <BorrowerSectorTable
              items={filtered}
              selectedId={selectedId}
              onSelect={openBorrower}
              preferThai={i18n.language.includes("th")}
            />
          </Box>
        </>
      )}

      {selectedId && (
        <>
          {/* Back to Borrowers — Figma 7258:26267; 40px above the detail (y 32 → 112) */}
          <Box display="flex" alignItems="center" gap="8px" mb="20px">
            <IconButton
              onClick={backToList}
              aria-label={t("Back-To-Borrowers")}
              sx={{ p: "8px", borderRadius: "20px" }}
            >
              <ArrowBackIcon
                sx={{ fontSize: "24px", color: "var(--dark)" }}
              />
            </IconButton>
            <Typography
              fontSize="20px"
              fontWeight={600}
              lineHeight="23.34px"
              color="var(--dark)"
            >
              {t("Back-To-Borrowers")}
            </Typography>
          </Box>
      {/* Container 7258:26733 — sits straight on the grey page, no card */}
      <Box flex={1} minWidth={0}>
        {detail ? (
          <>
            {/* Heading 7258:26448 */}
            <Box
              display="flex"
              alignItems="flex-start"
              justifyContent="space-between"
              mb="20px"
            >
              <Box
                display="flex"
                flexDirection="column"
                gap="12px"
                minWidth={0}
                role="group"
                aria-label={t("Classification-Card-Title")}
              >
                <Box display="flex" alignItems="center" gap="12px">
                  {/* Head/H1_Head: Bold 20/24 — a span, since alert.css forces h1–h4 sizes */}
                  <Typography
                    component="span"
                    fontSize="20px"
                    fontWeight={700}
                    lineHeight="24px"
                    color="var(--dark)"
                  >
                    {detailName}
                  </Typography>
                  {/* The detail endpoint has no activation; the list does. */}
                  {listItem && <BorrowerStatusPill active={listItem.activated} />}
                  {/* Listing status has no slot in Figma; it sits beside the status pill so a
                      long ISIC name never pushes it onto a line of its own (CCSME26-381). */}
                  <Box display="flex" alignItems="center" gap="8px">
                    <ListingStatusChip status={detail.listingStatus} />
                    {detail.classificationSource?.listingStatus === "borrower" && <ProvidedByBorrowerTag />}
                  </Box>
                </Box>
                {/* Borrower_Infomation 7457:13723 */}
                <Box
                  display="flex"
                  alignItems="center"
                  gap="12px"
                  flexWrap="wrap"
                >
                  <Typography {...infoText}>
                    {t("Tax-ID")}: {detail.taxId}
                  </Typography>
                  <InfoDivider />
                  <Typography {...infoText}>
                    {t("Col-Borrower-Type")}:{" "}
                    {isLegalEntity(detail.taxId)
                      ? t("Borrower-Type-Legal")
                      : t("Borrower-Type-Individual")}
                  </Typography>
                  <InfoDivider />
                  <Typography {...infoText}>
                    {t("Sector")}:{" "}
                    {detail.sectionCode
                      ? `${detail.sectionCode} - ${sectionLabel(detail, i18n.language, t("Sector-Unclassified"))}`
                      : t("Sector-Unclassified")}
                  </Typography>
                  <InfoDivider />
                  <Box display="flex" alignItems="center" gap="8px">
                    <Typography {...infoText}>
                      {t("ISIC")}: <span>{ownSectorLabel(detail)}</span>
                    </Typography>
                    {!!detail.sectorIsicCode && detail.classificationSource?.sectorIsic === "borrower" && (
                      <ProvidedByBorrowerTag />
                    )}
                  </Box>
                </Box>
              </Box>
              {/* Button 7258:26452 — white, 1px dark stroke, radius 4, 8/12 padding */}
              <Button
                variant="outlined"
                onClick={() => setOpenEditBorrower(true)}
                startIcon={<FeStylusIcon sx={{ fontSize: "24px !important" }} />}
                sx={{
                  flexShrink: 0,
                  minWidth: 0,
                  // the 1px stroke sits inside Figma's 86×40 box
                  px: "11px",
                  py: "7px",
                  borderRadius: "4px",
                  border: "1px solid var(--dark)",
                  bgcolor: "var(--white)",
                  color: "var(--dark)",
                  textTransform: "none",
                  fontSize: "16px",
                  fontWeight: 500,
                  lineHeight: "24px",
                  "& .MuiButton-startIcon": { m: 0, mr: "8px" },
                  "&:hover": { border: "1px solid var(--dark)", bgcolor: "var(--white)" },
                }}
              >
                {t("Edit")}
              </Button>
            </Box>

            <TabMenu
              type="underline"
              tabMenuData={subTabs}
              value={subTab}
              onChange={(k) => setSubTab(k as SubTab)}
            />

            <Box mt="20px">
              {subTab === "summary" && <DetailSummary borrower={detail} />}
              {subTab === "loans" && (
                <DetailLoans
                  key={detail.id}
                  borrower={detail}
                  editedLoan={editedLoan}
                  onAddLoan={() => setOpenAddLoan(true)}
                  onAddSnapshot={(loan) => setAddingSnapshotLoan(loan)}
                  onEditSnapshot={(loan, snapshot) =>
                    setEditingSnapshot({ loan, snapshot })
                  }
                />
              )}
              {subTab === "financial" && (
                <DetailFinancial
                  borrower={detail}
                  onAddYear={() => {
                    setEditingFinancial(null);
                    setOpenFinancial(true);
                  }}
                  onEditYear={(row) => {
                    setEditingFinancial(row);
                    setOpenFinancial(true);
                  }}
                />
              )}
              {subTab === "emissions" && (
                <DetailEmissions
                  borrower={detail}
                  onChanged={() => refreshDetail(detail.id)}
                />
              )}
            </Box>
          </>
        ) : (
          <Typography variant="body1" color="var(--gray1)">
            …
          </Typography>
        )}
      </Box>
        </>
      )}

      <AddBorrowerModal
        open={openAddBorrower}
        onClose={() => setOpenAddBorrower(false)}
        onSubmit={handleAddBorrower}
      />
      <EditBorrowerModal
        open={openEditBorrower}
        onClose={() => setOpenEditBorrower(false)}
        borrower={detail}
        onSubmit={handleEditBorrower}
      />
      <AddLoanModal
        open={openAddLoan}
        onClose={() => setOpenAddLoan(false)}
        borrowers={detail ? [{ id: detail.id, name: detail.name }] : []}
        lockedBorrowerId={detail?.id}
        onSubmit={handleAddLoan}
      />
      <AddSnapshotModal
        open={!!addingSnapshotLoan}
        onClose={() => setAddingSnapshotLoan(null)}
        loan={addingSnapshotLoan ?? undefined}
        onSubmit={handleAddSnapshot}
      />
      <EditSnapshotModal
        open={!!editingSnapshot}
        onClose={() => setEditingSnapshot(null)}
        loan={editingSnapshot?.loan}
        snapshot={editingSnapshot?.snapshot}
        onSubmit={handleEditSnapshot}
      />
      <FinancialDataModal
        open={openFinancial}
        onClose={() => setOpenFinancial(false)}
        borrowerName={detail?.name}
        existingYears={detail?.financials.map((f) => f.year) ?? []}
        editRow={editingFinancial}
        onSubmit={handleFinancialSubmit}
      />
    </Box>
  );
};

export default MasterDetail;
